import { Action } from './base'
import moment from 'moment'
import type { Moment } from 'moment'
import API from 'app/services/printout/printoutApi'
import { v4 as uuidv4 } from 'uuid'

import { SERVICE_TIME_FORMAT } from 'app/constants/date'
import { OutputType } from 'app/constants/outputType'
import { addTask } from './task'
import { TaskType } from 'app/constants/taskType'
import { getReportTypeByNumber } from 'app/services/printout/reportTypeResolver'
import { MessageType } from 'app/constants/messageType'
import { showMessage } from '@/modules/shared/stores/messageStore'
import Pair from 'app/common/pair'

export enum PrintoutActionTypeKeys {
  GET_PDF = 'GET_PDF',
  GET_OUTPUT_FILE = 'GET_OUTPUT_FILE',
  GET_ACTIVE_DRIVERS_REPORT = 'GET_ACTIVE_DRIVERS_REPORT',
  GET_AUDIT_REPORT = 'GET_AUDIT_REPORT',
  GET_SYNCHRONIZATION_REPORT = 'GET_SYNCHRONIZATION_REPORT',
  GET_TIMESHEET_REPORT = 'GET_TIMESHEET_REPORT',
  GET_TIMESHEET_DETAILED_REPORT = 'GET_TIMESHEET_DETAILED_REPORT',
  GET_DEMO_REPORT = 'GET_DEMO_REPORT',
  GET_REAL_INCOME_REPORT = 'GET_REAL_INCOME_REPORT',
}

export type GetPdfAction = Action<typeof PrintoutActionTypeKeys.GET_PDF, any>
export type GetOutputFileAction = Action<
  typeof PrintoutActionTypeKeys.GET_OUTPUT_FILE,
  any
>
export type GetActiveDriversReportAction = Action<
  typeof PrintoutActionTypeKeys.GET_ACTIVE_DRIVERS_REPORT,
  any
>
export type GetAuditReportAction = Action<
  typeof PrintoutActionTypeKeys.GET_AUDIT_REPORT,
  any
>
export type GetSynchronizationReportAction = Action<
  typeof PrintoutActionTypeKeys.GET_SYNCHRONIZATION_REPORT,
  any
>
export type GetDemoReportAction = Action<
  typeof PrintoutActionTypeKeys.GET_DEMO_REPORT,
  any
>
export type GetRealIncomeReportAction = Action<
  typeof PrintoutActionTypeKeys.GET_REAL_INCOME_REPORT,
  any
>
export type GetTimesheetReportAction = Action<
  typeof PrintoutActionTypeKeys.GET_TIMESHEET_REPORT,
  any
>
export type GetTimesheetDetailedReportAction = Action<
  typeof PrintoutActionTypeKeys.GET_TIMESHEET_DETAILED_REPORT,
  any
>

export type PrintActions =
  | GetPdfAction
  | GetOutputFileAction
  | GetActiveDriversReportAction
  | GetAuditReportAction
  | GetSynchronizationReportAction
  | GetDemoReportAction
  | GetTimesheetReportAction
  | GetTimesheetDetailedReportAction
  | GetRealIncomeReportAction

export const getOutputFile = async (id: string): Promise<any> => {
  const api = API.create()

  try {
    const response = await api.getOutputFile(id)
    const data = response.content.data

    return data
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const getPdf = async (
  companyId: string,
  reportType: number,
  drivers: Array<string>,
  vehicles: Array<string>,
  startTime: Moment,
  endTime: Moment,
  outputType: OutputType = OutputType.Pdf,
  params: any = {},
): Promise<any> => {
  const api = API.create()

  try {
    const id = uuidv4()

    const response = await api.getPdf(
      companyId,
      reportType,
      drivers,
      vehicles,
      moment(startTime).format(SERVICE_TIME_FORMAT),
      moment(endTime).format(SERVICE_TIME_FORMAT),
      outputType,
      id,
      params,
    )
    const data = response.content.data

    addTask(id, TaskType.Printout, getReportTypeByNumber(reportType))

    return data
  } catch (error) {
    if (error.status.status == 403) {
      return { error: error.status.data.code }
    }

    showMessage({
      text: `printout.processed-error`,
      details: `errorCode.${error.status.data.code}`,
      type: MessageType.ERROR,
      translate: true,
    })
    return { error: error.status.data.code }
  }
}

export const getPdfFromDates = async (
  companyId: string,
  reportType: number,
  drivers: Array<string>,
  vehicles: Array<string>,
  dates: Array<Moment>,
  outputType: OutputType = OutputType.Pdf,
  params: any = {},
): Promise<any> => {
  const api = API.create()

  try {
    const id = uuidv4()

    const response = await api.getPdfFromDates(
      companyId,
      reportType,
      drivers,
      vehicles,
      dates.map((d) => moment(d).format(SERVICE_TIME_FORMAT)),
      outputType,
      id,
      params,
    )
    const data = response.content.data

    addTask(id, TaskType.Printout, getReportTypeByNumber(reportType))

    return data
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const getRealIncomeReport = async (
  companies: Array<string>,
  period: Moment,
): Promise<any> => {
  const api = API.create()

  try {
    const response = await api.getRealIncomeReport(
      companies,
      period.format(SERVICE_TIME_FORMAT),
    )
    const data = response.content.data

    return data
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const getActiveDriversReport = async (
  companies: Array<string>,
  groups: Array<string>,
  months: Array<number>,
  year: number,
  outputType: OutputType = OutputType.Pdf,
  showDetails: boolean,
  showActiveness: boolean,
  showTimesheet: boolean,
  showBusinessTrip: boolean,
  showPrintout: boolean,
  showBasic: boolean,
  showPRO: boolean,
  showVPN: boolean,
  showUpdater: boolean,
  showCurrentlyActive: boolean,
): Promise<any> => {
  const api = API.create()

  try {
    const response = await api.getActiveDriversReport(
      companies,
      groups,
      months,
      year,
      outputType,
      showDetails,
      showActiveness,
      showTimesheet,
      showBusinessTrip,
      showPrintout,
      showBasic,
      showPRO,
      showVPN,
      showUpdater,
      showCurrentlyActive,
    )
    const data = response.content.data

    return data
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const getAuditReport = async (
  companies: Array<string>,
  groups: Array<string>,
  user: string,
  startDate: Moment,
  endDate: Moment,
  events: Array<string>,
  outputType: OutputType = OutputType.Pdf,
  sortBy: number,
): Promise<any> => {
  const api = API.create()

  try {
    const response = await api.getAuditReport(
      companies,
      groups,
      user,
      startDate,
      endDate,
      events,
      outputType,
      sortBy,
    )
    const data = response.content.data

    return data
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const getSynchronizationReport = async (
  companies: Array<string>,
  user: string,
  startDate: Moment,
  endDate: Moment,
  outputType: OutputType = OutputType.Pdf,
  sortBy: number,
  onlyUpdater: boolean,
): Promise<any> => {
  const api = API.create()

  try {
    const response = await api.getSynchronizationReport(
      companies,
      user,
      startDate,
      endDate,
      outputType,
      sortBy,
      onlyUpdater,
    )
    const data = response.content.data

    return data
  } catch (error) {
    return { error: error.status.data.code }
  }
}
export const getDemoReport = async (
  startDate: Moment,
  endDate: Moment,
  outputType: OutputType = OutputType.Pdf,
  sortBy: number,
): Promise<any> => {
  const api = API.create()

  try {
    const response = await api.getDemoReport(
      startDate,
      endDate,
      outputType,
      sortBy,
    )
    const data = response.content.data

    return data
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const getCompanyDataReport = async (
  companies: Array<string>,
  groups: Array<string>,
  months: Array<number>,
  year: number,
  outputType: OutputType = OutputType.Pdf,
  sortBy: number,
  onlyPro: boolean,
  onlyPackage: boolean,
  mergeSameCompanies: boolean,
  source: number,
  updatePaymentDate: boolean,
): Promise<any> => {
  const api = API.create()

  try {
    const response = await api.getCompanyDataReport(
      companies,
      groups,
      months,
      year,
      outputType,
      sortBy,
      onlyPro,
      onlyPackage,
      mergeSameCompanies,
      source,
      updatePaymentDate,
    )
    const data = response.content.data

    return data
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const getTimesheetReport = async (
  companies: Array<Pair<string, string>>,
  period: Moment,
): Promise<any> => {
  const api = API.create()

  try {
    const response = await api.getTimesheetReport(
      companies,
      moment(period).format(SERVICE_TIME_FORMAT),
    )
    const data = response.content.data

    return data
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const getTimesheetDetailedReport = async (
  companies: Array<Pair<string, string>>,
  period: Moment,
  detailed: boolean,
): Promise<any> => {
  const api = API.create()

  try {
    const response = await api.getTimesheetDetailedReport(
      companies,
      moment(period).format(SERVICE_TIME_FORMAT),
      detailed,
    )
    const data = response.content.data

    return data
  } catch (error) {
    return { error: error.status.data.code }
  }
}
